import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Cloudflare Turnstile widget
 *
 * The site key is public by design (it ships to every visitor's browser).
 * REACT_APP_TURNSTILE_SITE_KEY env var wins; the fallback keeps the widget
 * working without a Netlify build env var.
 */
const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAAFKFGbfjD9HdFjXI';
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v1/api.js?render=explicit';

let scriptPromise = null;

function loadTurnstile() {
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = resolve;
      script.onerror = () => {
        scriptPromise = null; // allow retry on next mount
        reject(new Error('Turnstile script failed to load'));
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
}

/**
 * Renders an invisible-friendly Turnstile widget.
 * onVerify is called with the token (null when the challenge expired or errored).
 */
const TurnstileWidget = ({ onVerify }) => {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [loadError, setLoadError] = useState(null);

  const handleVerify = useCallback(
    (token) => {
      if (onVerify) onVerify(token);
    },
    [onVerify]
  );

  useEffect(() => {
    let cancelled = false;

    loadTurnstile()
      .then(() => {
        if (cancelled || !containerRef.current || widgetIdRef.current !== null) return;
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: (token) => handleVerify(token),
          'expired-callback': () => handleVerify(null),
          'error-callback': () => handleVerify(null),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('Security check failed to load. Please check your connection and refresh the page.');
          handleVerify(null);
        }
      });

    return () => {
      cancelled = true;
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch (e) {
          // widget already removed (e.g. page unload)
        }
      }
      widgetIdRef.current = null;
    };
  }, [handleVerify]);

  return (
    <div>
      <div ref={containerRef} className="flex justify-center min-h-[65px]" />
      {loadError && <p className="text-xs text-red-600 mt-1 text-center">{loadError}</p>}
    </div>
  );
};

export default TurnstileWidget;
